import { isValidDate } from 'Utils/dateUtils';
import { isEmpty } from 'Utils/lodashUtils';

const format = require('string-template');

function isEmptyCheck(value1, value2, elementTypeName) {
  if (elementTypeName === 'DATE' || elementTypeName === 'CURRENCY') {
    return (value1 === undefined || value1 === '') && (value2 === undefined || value2 === '');
  }
  if (value1 === undefined || (Array.isArray(value1) && value1.length === 0)) {
    return true;
  }
  return false;
}

function isMatchingRegex(currentValue, regex, elementTypeName) {
  const value1 = currentValue.v1;
  const value2 = currentValue.v2;
  if (elementTypeName === 'CURRENCY') {
    return new RegExp(regex).test(value1) && new RegExp(regex).test(value2);
  } else if (elementTypeName === 'CC_NUMBER') {
    return (
      (value1 !== undefined && new RegExp(regex).test(value1)) ||
      (value2 !== undefined && new RegExp(regex).test(value2))
    );
  } else {
    return new RegExp(regex).test(value1);
  }
}

export function validateFiltersAgainstValidationRule(filters = [], currentValues = [], dateFormat) {
  return new Promise((resolve, reject) => {
    try {
      const modifedCurrentValues = filters
        .map((filter) => {
          const validations = filter.validations;

          const currentValue = currentValues.find((valueIterator) => {
            return valueIterator.id === filter.elementId;
          });

          if (filter.elementMandatory && currentValue !== undefined) {
            return currentValue;
          }

          const requiredValidation = validations.find((validation) => {
            return validation.validationRuleKey === 'REQUIRED_VALIDATION';
          });
          if (currentValue !== undefined) {
            let validationErrors;
            if (requiredValidation && isEmptyCheck(currentValue.v1, currentValue.v2, filter.elementTypeName)) {
              validationErrors = Object.assign({}, currentValue, {
                error: format(requiredValidation.translatedErrorMessage, filter.elementLabel),
                flag: true,
              });
            } else {
              const validationDetails = validations.find((validation) => {
                if (
                  validation.validationRef &&
                  !isMatchingRegex(currentValue, validation.validationRef, filter.elementTypeName)
                ) {
                  return true;
                } else if (validation.validationRuleKey === 'CC_VALIDATION') {
                  if (
                    (currentValue.v1 !== undefined && currentValue.v2 !== undefined) ||
                    (currentValue.v1 !== undefined && currentValue.v1.length !== 6) ||
                    (currentValue.v2 !== undefined && currentValue.v2.length !== 4)
                  ) {
                    return true;
                  }
                } else if (validation.validationRuleKey === 'DATE_VALIDATION') {
                  if (
                    currentValue.type === 'BETWEEN' &&
                    !isEmptyCheck(currentValue.v1, currentValue.v2, filter.elementTypeName)
                  ) {
                    if (!isValidDate(currentValue.v1, dateFormat) || !isValidDate(currentValue.v2, dateFormat)) {
                      return true;
                    }
                  }
                } else if (validation.validationRuleKey === 'RANGE_VALIDATION') {
                  if (!isEmptyCheck(currentValue.v1, currentValue.v2, filter.elementTypeName)) {
                    if (filter.elementTypeName === 'DATE') {
                      const fromDate = new Date(currentValue.v1);
                      const ToDate = new Date(currentValue.v2);
                      if (fromDate > ToDate) {
                        return true;
                      }
                    } else {
                      const fromValue = Number(currentValue.v1);
                      const toValue = Number(currentValue.v2);
                      if (fromValue > toValue) {
                        return true;
                      }
                    }
                  }
                }
                return false;
              });
              if (validationDetails) {
                validationErrors = Object.assign({}, currentValue, {
                  error: format(validationDetails.translatedErrorMessage, filter.elementLabel),
                  flag: true,
                });
              }
            }
            return (
              validationErrors ||
              Object.assign(currentValue, {
                error: '',
                flag: false,
              })
            );
          } else if (requiredValidation) {
            return Object.assign(
              {},
              {
                id: filter.elementId,
                error: format(requiredValidation.translatedErrorMessage, filter.elementLabel),
                flag: true,
              },
            );
          }
          return undefined;
        })
        .filter((item) => item !== undefined);
      resolve(modifedCurrentValues);
    } catch (error) {
      reject(error);
    }
  });
}

const getSpecialCharacterName = (specialCharacter) => {
  switch (specialCharacter) {
    case '-':
      return 'hyphen';
    default:
      return specialCharacter;
  }
};

export const alphaNumeric = (value, otherAllowedChars = '', errorMsg = '') => {
  const regex = new RegExp(`^[a-zA-Z0-9${otherAllowedChars}]*$`);
  const otherAllowedString = otherAllowedChars.split('').join(',').replace(' ', 'spaces');
  if (!regex.test(value)) {
    if (!isEmpty(errorMsg)) {
      return errorMsg;
    } else {
      return otherAllowedChars
        ? `Please enter a combination of alphanumeric characters and a ${getSpecialCharacterName(otherAllowedString)}.`
        : 'Special characters and spaces are not allowed in this field.';
    }
  } else return null;
};

export const validateNumericWithCommas = (value, otherAllowedChars = ',') => {
  const regex = new RegExp(`^[0-9${otherAllowedChars}]*$`);
  if (!regex.test(value)) {
    return 'Please enter alphanumeric without special characters.';
  } else return null;
};

export const numeric = (value) => {
  const regex = new RegExp('^[0-9]*$');
  if (!regex.test(value)) {
    return 'Please enter numeric characters only.';
  } else return null;
};

export const commaSeperatedNumericValues = (value) => {
  const regex = new RegExp('^[0-9]+(?:,[0-9]+)*$');
  if (!regex.test(value)) {
    return 'Please enter multiple MIDs, separate by commas.';
  } else return null;
};

export const decimalNumber = (value, noOfDigits = 0) => {
  const numberDigitsLimit = value?.includes('.') ? noOfDigits + 1 : noOfDigits - 2;
  if (
    noOfDigits &&
    (value?.length > numberDigitsLimit || (value?.includes('.') && value?.split('.')[0]?.length > noOfDigits - 2))
  ) {
    return `Maximum character limit exceeded. Please enter a number with no more than ${
      noOfDigits - 2
    } whole digits and 2 decimal places.`;
  }
  const regex = /^\d+(\.\d{1,2})?$/;
  if (!regex.test(value)) {
    return 'Please enter a numeric value in 00000/00000.00 format only.';
  } else return null;
};

export const removeInvalidXMLChars = (str) => {
  let regex1 = new RegExp(
    '/((?:[\0-\x08\x0B\f\x0E-\x1F\uFFFD\uFFFE\uFFFF]|[\uD800-\uDBFF](?![\uDC00-\uDFFF])|(?:[^\uD800-\uDBFF]|^)[\uDC00-\uDFFF]))/',
    'g',
  );
  let regex2 = new RegExp(
    '([\\x7F-\\x84]|[\\x86-\\x9F]|[\\uFDD0-\\uFDEF]|(?:\\uD83F[\\uDFFE\\uDFFF])|(?:\\uD87F[\\uDF' +
      'FE\\uDFFF])|(?:\\uD8BF[\\uDFFE\\uDFFF])|(?:\\uD8FF[\\uDFFE\\uDFFF])|(?:\\uD93F[\\uDFFE\\uD' +
      'FFF])|(?:\\uD97F[\\uDFFE\\uDFFF])|(?:\\uD9BF[\\uDFFE\\uDFFF])|(?:\\uD9FF[\\uDFFE\\uDFFF])' +
      '|(?:\\uDA3F[\\uDFFE\\uDFFF])|(?:\\uDA7F[\\uDFFE\\uDFFF])|(?:\\uDABF[\\uDFFE\\uDFFF])|(?:\\' +
      'uDAFF[\\uDFFE\\uDFFF])|(?:\\uDB3F[\\uDFFE\\uDFFF])|(?:\\uDB7F[\\uDFFE\\uDFFF])|(?:\\uDBBF' +
      '[\\uDFFE\\uDFFF])|(?:\\uDBFF[\\uDFFE\\uDFFF])(?:[\\0-\\t\\x0B\\f\\x0E-\\u2027\\u202A-\\uD7FF\\' +
      'uE000-\\uFFFF]|[\\uD800-\\uDBFF][\\uDC00-\\uDFFF]|[\\uD800-\\uDBFF](?![\\uDC00-\\uDFFF])|' +
      '(?:[^\\uD800-\\uDBFF]|^)[\\uDC00-\\uDFFF]))',
    'g',
  );
  return String(str || '')
    .replace(regex1, ' ')
    .replace(regex2, ' ');
};

export const removeInvalidCharacters = (str, language = 'English') => {
  let regex = language === 'English' ? /[^a-zA-Z0-9 !@#$%&*()+\-={}'<>.?\r\n]/g : /[`~^_[\]:;",/\\|]/g;
  return removeInvalidXMLChars(str)
    .replace(regex, ' ')
    .replace(/[\t ]{2,}/g, ' ');
};

export default {
  validateFiltersAgainstValidationRule,
};
